// Number Bonds generator: "known + ? = target". Integers to 10, 20, 100, 1000, then
// decimals to 1 and fractions to 1. Choices always include the answer and are distinct.

import { int, pick, shuffle, type Rng } from '@kit/rng';
import type { SkillId } from '@shell/types';

export type BondKind = 'int' | 'decimal' | 'fraction';

export interface BondProblem {
  kind: BondKind;
  skill: SkillId;
  /** display strings */
  target: string;
  known: string;
  answer: string;
  choices: string[];
  /** numeric values (fractions carry a denominator) */
  targetValue: number;
  knownValue: number;
  answerValue: number;
  denominator?: number;
  hint: string;
}

const CHOICES = 6;

function fmtDec(v: number, places: number): string {
  return v.toFixed(places);
}

function intChoices(rng: Rng, answer: number, lo: number, hi: number, step: number): string[] {
  const set = new Set<number>([answer]);
  const deltas = [step, -step, 2 * step, -2 * step, 3 * step, -3 * step, 5 * step, -5 * step, 10 * step, -10 * step];
  let guard = 0;
  while (set.size < CHOICES && guard++ < 200) {
    const d = guard <= deltas.length ? deltas[guard - 1] : int(rng, -12, 12) * step;
    const v = answer + d;
    if (v >= lo && v <= hi && !set.has(v)) set.add(v);
  }
  // fill with any in-range values if the range was tight
  guard = 0;
  while (set.size < CHOICES && guard++ < 500) {
    const v = lo + int(rng, 0, Math.floor((hi - lo) / step)) * step;
    if (!set.has(v)) set.add(v);
  }
  return shuffle(rng, [...set]).map(String);
}

export function makeBond(level: number, rng: Rng): BondProblem {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 6 || (L <= 8 && rng() < 0.5)) {
    let target: number, step = 1;
    if (L <= 2) target = 10;
    else if (L <= 4) target = rng() < 0.3 ? 10 : 20;
    else if (L === 5) { target = 100; step = 5; }
    else if (L === 6) target = 100;
    else if (L === 7) { target = 1000; step = 25; }
    else { target = 1000; step = 5; }
    const known = int(rng, 0, target / step) * step;
    const answer = target - known;
    const choices = intChoices(rng, answer, 0, target, step);
    const hint = answer <= 12 && target <= 20
      ? `Count up from ${known}: ${Array.from({ length: Math.min(answer, 5) }, (_, i) => known + i + 1).join(', ')}${answer > 5 ? '...' : ''}. How many steps to ${target}?`
      : `Think: ${known} and how much more makes ${target}? Try ${target} take away ${known}.`;
    return { kind: 'int', skill: 'add_sub', target: String(target), known: String(known), answer: String(answer), choices, targetValue: target, knownValue: known, answerValue: answer, hint };
  }
  if (L <= 8) {
    // decimals to 1: one place at L7, two places at L8
    const places = L === 7 ? 1 : 2;
    const unit = places === 1 ? 10 : 100;
    const k = int(rng, 1, unit - 1);
    const a = unit - k;
    const known = k / unit, answer = a / unit;
    const set = new Set<number>([a]);
    let guard = 0;
    while (set.size < CHOICES && guard++ < 300) {
      const v = a + pick(rng, [1, -1, 2, -2, 5, -5, 10, -10, int(rng, -20, 20)]);
      if (v >= 0 && v <= unit && !set.has(v)) set.add(v);
    }
    const choices = shuffle(rng, [...set]).map(v => fmtDec(v / unit, places));
    return { kind: 'decimal', skill: 'add_sub', target: '1', known: fmtDec(known, places), answer: fmtDec(answer, places), choices, targetValue: 1, knownValue: known, answerValue: answer, hint: `${fmtDec(known, places)} is ${k} ${places === 1 ? 'tenths' : 'hundredths'}. How many more ${places === 1 ? 'tenths' : 'hundredths'} make ${unit}?` };
  }
  // fractions to 1
  const d = L === 9 ? pick(rng, [2, 3, 4, 5, 6, 8, 10]) : pick(rng, [3, 4, 5, 6, 7, 8, 9, 10, 12]);
  const k = int(rng, 1, d - 1);
  const a = d - k;
  const set = new Set<number>([a]);
  for (let n = 1; n < d && set.size < CHOICES; n++) set.add(n);
  let guard = 0;
  while (set.size < CHOICES && guard++ < 50) set.add(int(rng, 1, d + 2));
  const choices = shuffle(rng, [...set]).map(n => `${n}/${d}`);
  const name = d === 2 ? 'halves' : d === 3 ? 'thirds' : d === 4 ? 'fourths' : d === 5 ? 'fifths' : d === 6 ? 'sixths' : d === 7 ? 'sevenths' : d === 8 ? 'eighths' : d === 9 ? 'ninths' : d === 10 ? 'tenths' : 'twelfths';
  return { kind: 'fraction', skill: 'fractions', target: '1', known: `${k}/${d}`, answer: `${a}/${d}`, choices, targetValue: 1, knownValue: k / d, answerValue: a / d, denominator: d, hint: `One whole is ${d} ${name}. You have ${k} ${name}. How many more ${name}?` };
}
